import StudyProgramsFilterList from "@common/chapters/StudyProgramsFilterList"
import IconButtonM from "@common/components/button/IconButtonM"
import { ModalM } from "@common/components/modal/ModalM"
import TextM from "@common/components/text/TextM"
import StudyProgramDisplayComponent from "@common/studyPrograms/components/StudyProgramDisplayComponent"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { hasRole } from "@data/utility/ability"
import moment from "moment"
import React, { useContext, useMemo, useState } from "react"
import { View } from "react-native"
import { connect } from "react-redux"

/**
 * Chapter of the day: the program item scheduled today (start_date + day - 1),
 * preferring one whose subject matches `preferredSubjectIds` (e.g. the
 * session's pedagogical objective) when several chapters share the day.
 */
export function resolveTodayChapterId(studyProgram, preferredSubjectIds = []) {
    const items = studyProgram?.study_program_items
    if (!Array.isArray(items) || !studyProgram?.start_date) return null
    const todayItems = items.filter((item) =>
        moment(studyProgram.start_date)
            .add((item?.day ?? 1) - 1, "days")
            .isSame(moment(), "day")
    )
    if (todayItems.length === 0) return null
    const preferred = todayItems.find((item) =>
        preferredSubjectIds?.some(
            (id) => id == item?.chapter?.school_subject_id
        )
    )
    return (preferred ?? todayItems[0])?.chapter?.id ?? null
}

const StudyProgramChapterSelectionModal = (props) => {
    const {
        visible,
        onDismiss,
        onSelectChapter,
        studentId = null,
        title = "Sélectionner un chapitre du programme",
        // Read-only mode (e.g. from the student brief): chapters are not
        // selectable, the modal is a plain program viewer.
        readOnly = false,
        // Subject ids used to pick the relevant "chapter of the day" to
        // highlight (typically the session objective's subject_ids).
        highlightSubjectIds = null,
    } = props

    const dimensions = useContext(DimensionsContext)
    const [selectedStudyProgramId, setSelectedStudyProgramId] = useState(null)

    const currentStudyProgram = props.studyPrograms?.find(
        (s) => s.id === selectedStudyProgramId
    )

    const highlightedChapterId = useMemo(
        () =>
            highlightSubjectIds
                ? resolveTodayChapterId(
                      currentStudyProgram,
                      highlightSubjectIds
                  )
                : null,
        [currentStudyProgram, highlightSubjectIds]
    )

    const handleStudyProgramChapterSelect = (item) => {
        if (readOnly) return
        if (item?.chapter) {
            onSelectChapter(item.chapter)
            onDismiss()
        }
    }

    return (
        <ModalM
            visible={visible}
            onDismiss={onDismiss}
            style={{
                height: "90%",
                maxWidth: "90%",
                width: "90%",
            }}
        >
            <View style={{ height: "100%", width: "100%" }}>
                {/* Header */}
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        padding: 20,
                    }}
                >
                    <IconButtonM
                        icon={"times"}
                        iconColor={theme.colors.primary}
                        containerColor={theme.colors.white}
                        style={{}}
                        onPress={onDismiss}
                        size={28}
                    />
                    <TextM
                        fontSize={18}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={{ marginLeft: 10 }}
                    >
                        {title}
                    </TextM>
                </View>

                {/* Study Program Content */}
                <View
                    style={{
                        flex: 1,
                    }}
                >
                    <StudyProgramsFilterList
                        studentIds={
                            hasRole(props.user, "student")
                                ? null
                                : studentId?.toString()
                        }
                        setSelectedStudyProgramId={setSelectedStudyProgramId}
                        selectedStudyProgramId={selectedStudyProgramId}
                    />
                    {selectedStudyProgramId && (
                        <View
                            style={{
                                flex: 1,
                                alignSelf: "center",
                                width: dimensions.width - 40,
                            }}
                        >
                            <StudyProgramDisplayComponent
                                studyProgram={currentStudyProgram}
                                isLoading={false}
                                loadingMessage="Chargement du programme de révision..."
                                onPress={handleStudyProgramChapterSelect}
                                showChapterProgress={
                                    !hasRole(props.user, "student") &&
                                    !studentId?.toString().includes(",")
                                }
                            />
                        </View>
                    )}
                </View>
            </View>
        </ModalM>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        studyPrograms: state.data.studyPrograms?.studyPrograms,
    }
}

export default connect(mapStateToProps)(StudyProgramChapterSelectionModal)
